import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MapPin, Plane, TrainFront, Lock, ArrowUpDown, X, CalendarClock,
  Users, Briefcase, Minus, Plus, CreditCard, AlertCircle, ChevronRight,
} from "lucide-react";
import MapView from "@/components/MapView";
import PlacePickerModal from "@/components/PlacePickerModal";
import Modal from "@/components/Modal";
import Button from "@/components/Button";
import DateTimeFields from "@/components/DateTimeFields";
import { NativeSelect } from "@/components/Form";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { AIRLINES } from "@/lib/vehicles";
import { cn } from "@/lib/format";

export const PENDING_RIDE_KEY = "getaride_pending_ride";

const isAirport = (p) => !!p && !!p.airport;
const isStation = (p) => !!p && !!p.station;
const isHub = (p) => isAirport(p) || isStation(p);
const hubIcon = (p) => (isAirport(p) ? Plane : isStation(p) ? TrainFront : MapPin);

function Counter({ icon: Icon, label, value, onDec, onInc, testID }) {
  return (
    <div className="flex items-center justify-between rounded-xl bg-secondary px-4 py-3">
      <span className="flex items-center gap-2 font-semibold text-foreground">
        <Icon className="h-5 w-5 text-brand-600" /> {label}
      </span>
      <div className="flex items-center gap-4">
        <button onClick={onDec} data-testid={`${testID}-minus`} className="flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card text-foreground">
          <Minus className="h-4 w-4" />
        </button>
        <span data-testid={`${testID}-count`} className="w-6 text-center font-num text-xl font-bold text-foreground">{value}</span>
        <button onClick={onInc} data-testid={`${testID}-plus`} className="flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card text-foreground">
          <Plus className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function LocationRow({ icon: Icon, color, label, placeholder, onClick, onRemove, locked, testID }) {
  return (
    <div className="flex items-center gap-3 py-3" data-testid={testID}>
      <Icon className="h-4 w-4 shrink-0" style={{ color }} />
      <button onClick={locked ? undefined : onClick} className={cn("flex-1 truncate text-left text-sm font-medium", placeholder ? "text-muted-foreground" : "text-foreground")}>
        {label}
      </button>
      {locked ? (
        <Lock className="h-4 w-4 text-muted-foreground" />
      ) : onRemove ? (
        <button onClick={onRemove}><X className="h-4 w-4 text-muted-foreground" /></button>
      ) : (
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      )}
    </div>
  );
}

export default function BookWidget({ guest = false, heightClass = "h-full" }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pickup, setPickup] = useState(null);
  const [destination, setDestination] = useState(null);
  const [stops] = useState([]);
  const [scheduledAt, setScheduledAt] = useState("");
  const [picker, setPicker] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [needCard, setNeedCard] = useState(false);
  const [showAirport, setShowAirport] = useState(false);

  const [airline, setAirline] = useState("");
  const [flightNumber, setFlightNumber] = useState("");
  const [passengers, setPassengers] = useState(1);
  const [bags, setBags] = useState(1);

  useEffect(() => {
    if (!guest && user?.role === "customer") {
      api("/payments/method").then((m) => setNeedCard(!!m.enabled && !m.has_card)).catch(() => {});
    }
  }, [guest, user]);

  const fromAirport = isAirport(pickup);
  const hubIsAirport = isAirport(pickup) || isAirport(destination);
  const isHubTrip = isHub(pickup) || isHub(destination);
  // Airline/flight details are only relevant for the MCO airport hub, not Brightline.
  const detailsReady = isHubTrip && (!hubIsAirport || (!!airline && (!fromAirport || flightNumber.trim().length >= 2)));

  const onSelectPlace = (p) => {
    setError(null);
    if (picker === "pickup") setPickup(p);
    else if (picker === "destination") setDestination(p);
  };

  const swap = () => {
    const p = pickup;
    setPickup(destination);
    setDestination(p);
  };

  const classForParty = (pax, b) =>
    pax <= 3 && b <= 3 ? "Economy · up to 3 riders, 3 bags"
      : pax <= 4 && b <= 4 ? "SUV · up to 4 riders, 4 bags"
      : "Executive SUV · up to 6 riders, 6 bags";

  const openScheduler = () => {
    setError(null);
    if (!pickup || !destination) return setError("Please set your pickup and drop-off locations.");
    if (isHub(pickup) && isHub(destination)) return setError("Only one end of the trip can be the airport or station.");
    if (!isHubTrip) return setError("One end of your trip must be Orlando International Airport (MCO) or Brightline Orlando Station.");
    if (!scheduledAt) return setError("Please choose a date and time for your ride.");
    const t = new Date(scheduledAt).getTime();
    if (t < Date.now() + 24 * 3600e3) return setError("Rides must be booked at least 24 hours in advance.");
    if (t > Date.now() + 90 * 864e5) return setError("Rides can be scheduled up to 90 days in advance.");
    setShowAirport(true);
  };

  const scheduleRide = async () => {
    if (!pickup || !destination) return setError("Please set your pickup and drop-off locations.");
    if (isHub(pickup) && isHub(destination)) return setError("Only one end of the trip can be the airport or station.");
    if (!isHubTrip) return setError("One end of your trip must be Orlando International Airport (MCO) or Brightline Orlando Station.");
    if (!detailsReady) return setError(fromAirport ? "Please add your arrival flight, airline and bag count." : "Please add your airline and bag count.");
    if (!scheduledAt) return setError("Please choose a date and time for your ride.");
    const t = new Date(scheduledAt).getTime();
    if (t < Date.now() + 24 * 3600e3) return setError("Rides must be booked at least 24 hours in advance.");
    if (t > Date.now() + 90 * 864e5) return setError("Rides can be scheduled up to 90 days in advance.");

    const payload = {
      pickup, destination, stops, when: "scheduled",
      scheduled_time: new Date(scheduledAt).toISOString(),
      passengers, bags,
      origin_url: window.location.origin,
      airport_info: hubIsAirport ? {
        direction: fromAirport ? "from" : "to",
        airline, bags,
        flight_number: fromAirport ? flightNumber.trim() : null,
        terminal: null,
        baggage_claim: null,
      } : null,
    };

    if (guest) {
      localStorage.setItem(PENDING_RIDE_KEY, JSON.stringify(payload));
      navigate("/auth");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await api("/rides", { method: "POST", body: payload });
      navigate(`/ride/${res.ride.id}`);
    } catch (e) {
      setError(e?.message || "Could not schedule this ride. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const nowLocalMin = new Date(Date.now() + 24 * 3600e3 - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16);

  return (
    <div className={cn("flex flex-col md:flex-row", heightClass)}>
      {/* Map */}
      <div className="h-[38vh] w-full shrink-0 md:h-full md:flex-1">
        <MapView pickup={pickup} destination={destination} stops={stops} interactive ambientCars={5} onPickupChange={(c) => !isHub(pickup) && setPickup({ ...c, label: pickup.label })} />
      </div>

      {/* Panel */}
      <div className="flex w-full flex-col overflow-y-auto border-t border-border bg-background p-5 md:w-[440px] md:border-l md:border-t-0">
        <h1 className="text-2xl font-extrabold text-foreground">{guest || !user?.name ? "Where to?" : `Hi ${user.name.split(" ")[0]}!`}</h1>

        {/* Schedule time */}
        <div className="mt-3 flex items-center gap-1.5 rounded-full bg-accent px-3 py-1.5 text-xs font-semibold text-brand-700 dark:text-brand-400">
          <CalendarClock className="h-3.5 w-3.5 shrink-0" /> Ready for your trip? Schedule your ride below!
        </div>
        <div className="mt-3">
          <DateTimeFields
            min={nowLocalMin}
            onChange={(v) => { setScheduledAt(v); setError(null); }}
          />
        </div>

        {/* Locations */}
        <div className="relative mt-4 rounded-2xl bg-secondary px-4 pr-12">
          <LocationRow
            icon={hubIcon(pickup)}
            color={isHub(pickup) ? "#9333ea" : "#22c55e"}
            label={pickup?.label || "Pickup location"}
            placeholder={!pickup}
            locked={isHub(pickup)}
            onClick={() => setPicker("pickup")}
            testID="pickup-row"
          />
          <div className="border-t border-border">
            <LocationRow
              icon={hubIcon(destination)}
              color="#9333ea"
              label={destination?.label || "Where are you going?"}
              placeholder={!destination}
              locked={isHub(destination)}
              onClick={() => setPicker("destination")}
              testID="destination-row"
            />
          </div>
          <button onClick={swap} data-testid="swap-direction" className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-card text-brand-600">
            <ArrowUpDown className="h-4 w-4" />
          </button>
        </div>

        <button onClick={openScheduler} data-testid="find-rides" className="mt-4 w-full rounded-2xl bg-brand-600 py-4 text-base font-bold text-white shadow-brand transition-all duration-200 hover:bg-brand-700 active:scale-[0.99]">
          Schedule Ride
        </button>
        {guest && <p className="mt-2 text-center text-xs text-muted-foreground">You&apos;ll be asked to sign in to confirm your ride.</p>}

        {!guest && needCard && (
          <button onClick={() => navigate("/payment-methods")} data-testid="add-card-banner" className="mt-3 flex items-center gap-2 rounded-xl bg-accent px-4 py-3 text-sm font-semibold text-brand-700 dark:text-brand-400">
            <CreditCard className="h-4 w-4" /> Add a payment method for faster checkout <ChevronRight className="ml-auto h-4 w-4" />
          </button>
        )}

        {error && !showAirport && (
          <div className="mt-3 flex items-center gap-2 rounded-xl bg-destructive/10 px-3 py-2.5 text-sm font-medium text-destructive" data-testid="ride-error">
            <AlertCircle className="h-4 w-4 shrink-0" /> {error}
          </div>
        )}
      </div>

      <Modal
        open={showAirport}
        onClose={() => setShowAirport(false)}
        title={!hubIsAirport ? "Add trip details" : fromAirport ? "Add airport pickup details" : "Add airport drop-off details"}
        size="md"
        footer={
          <Button className="w-full" size="lg" onClick={scheduleRide} loading={loading} data-testid="confirm-schedule">
            Schedule Ride
          </Button>
        }
      >
        <div className="space-y-3">
          {hubIsAirport && (
            <NativeSelect label="Airline" options={AIRLINES} placeholder="Select airline" value={airline} onChange={setAirline} data-testid="airline-select" />
          )}
          {hubIsAirport && fromAirport && (
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-foreground/80">Flight #</span>
              <input data-testid="flight-input" value={flightNumber} onChange={(e) => setFlightNumber(e.target.value.toUpperCase())} placeholder="AA1234" className="h-12 w-full rounded-xl border border-border bg-card px-3 text-sm text-foreground outline-none focus:border-brand-500" />
            </label>
          )}
          <Counter icon={Users} label="Passengers" value={passengers} onDec={() => setPassengers((p) => Math.max(1, p - 1))} onInc={() => setPassengers((p) => Math.min(6, p + 1))} testID="pax" />
          <Counter icon={Briefcase} label="Bags" value={bags} onDec={() => setBags((b) => Math.max(0, b - 1))} onInc={() => setBags((b) => Math.min(6, b + 1))} testID="bags" />
          <p className="flex items-center justify-center gap-1.5 text-sm font-semibold text-brand-600">{classForParty(passengers, bags)}</p>
          {error && (
            <div className="flex items-center gap-2 rounded-xl bg-destructive/10 px-3 py-2.5 text-sm font-medium text-destructive" data-testid="ride-error-overlay">
              <AlertCircle className="h-4 w-4 shrink-0" /> {error}
            </div>
          )}
        </div>
      </Modal>

      <PlacePickerModal
        open={picker !== null}
        title={picker === "pickup" ? "Set pickup" : "Set destination"}
        onlyHub={picker === "destination" ? (!!pickup && !isHub(pickup)) : picker === "pickup" ? (!!destination && !isHub(destination)) : false}
        blockHub={picker === "destination" ? isHub(pickup) : picker === "pickup" ? isHub(destination) : false}
        onClose={() => setPicker(null)}
        onSelect={onSelectPlace}
      />
    </div>
  );
}
